import CpfInput from "@/components/auth/CpfInput";
import { formatCnpjDisplay, normalizeCnpjDigits, validateClinicTaxId } from "@/domains/multi/cnpjValidation";
import { ClinicTaxTypes } from "@/domains/multi/clinicRoles";
import type { ClinicTaxType } from "@/domains/multi/clinicTypes";
import { Select } from "@/domains/shared/components/base/select/select";
import type { SelectItemType } from "@/domains/shared/components/base/select/select-shared";
import { Input } from "@/domains/shared/components/Input";
import { Label } from "@/domains/shared/components/Label";
import { isValidCpf } from "@/domains/shared/validation/cpf";
import { getTranslation } from "@mivo/i18n";
import { CLINIC_TAX_IDENTITY_TRANSLATIONS as T } from "./clinicUiConstants";

export function clinicTaxTypeItems(): SelectItemType[] {
    return [
        { id: ClinicTaxTypes.Cnpj, label: getTranslation(T.cnpjOption) },
        { id: ClinicTaxTypes.Cpf, label: getTranslation(T.cpfOption) },
    ];
}

export type ClinicTaxIdentityFieldsProps = {
    clinicTaxType: ClinicTaxType | "";
    clinicName: string;
    clinicCpfCnpj: string;
    clinicPhone: string;
    userCpf?: string | null;
    userName?: string;
    cpfLocked?: boolean;
    readOnlyEmail?: string;
    idPrefix?: string;
    onTaxTypeChange: (type: ClinicTaxType | "") => void;
    onClinicNameChange: (value: string) => void;
    onCpfCnpjChange: (value: string) => void;
    onPhoneChange: (value: string) => void;
};

export function ClinicTaxIdentityFields({
    clinicTaxType,
    clinicName,
    clinicCpfCnpj,
    clinicPhone,
    userCpf,
    userName,
    cpfLocked = false,
    readOnlyEmail,
    idPrefix = "clinic",
    onTaxTypeChange,
    onClinicNameChange,
    onCpfCnpjChange,
    onPhoneChange,
}: ClinicTaxIdentityFieldsProps) {
    const taxItems = clinicTaxTypeItems();

    return (
        <>
            {readOnlyEmail ? (
                <div>
                    <Label htmlFor={`${idPrefix}-email`} className="text-sm font-medium text-foreground">
                        {getTranslation(T.email)}
                    </Label>
                    <Input
                        id={`${idPrefix}-email`}
                        type="email"
                        value={readOnlyEmail}
                        readOnly
                        className="mt-2 rounded-xl bg-muted/40"
                        data-testid={`${idPrefix}-email`}
                    />
                </div>
            ) : null}

            <div>
                <Label className="text-sm font-medium text-foreground">
                    {getTranslation(T.taxTypeLabel)}
                </Label>
                <Select
                    data-testid={`${idPrefix}-tax-type-select`}
                    items={taxItems}
                    value={clinicTaxType || undefined}
                    onValueChange={(key) => {
                        const next = key != null && String(key) !== "" ? (String(key) as ClinicTaxType) : "";
                        onTaxTypeChange(next);
                        if (next !== ClinicTaxTypes.Cpf) {
                            onCpfCnpjChange("");
                        } else {
                            onCpfCnpjChange((userCpf ?? "").replace(/\D/g, ""));
                            if (userName) {
                                onClinicNameChange(userName);
                            }
                        }
                    }}
                    placeholder={getTranslation(T.taxTypePlaceholder)}
                    aria-label={getTranslation(T.taxTypeAria)}
                    className="mt-2 w-full"
                >
                    {(item) => <Select.Item id={item.id} label={item.label} textValue={item.label ?? ""} />}
                </Select>
            </div>

            <div>
                <Label htmlFor={`${idPrefix}-name`} className="text-sm font-medium text-foreground">
                    {getTranslation(T.clinicName)}
                </Label>
                <Input
                    id={`${idPrefix}-name`}
                    value={clinicName}
                    onChange={(e) => onClinicNameChange(e.target.value)}
                    required
                    className="mt-2 rounded-xl"
                    data-testid={`${idPrefix}-name`}
                />
            </div>

            {clinicTaxType === ClinicTaxTypes.Cpf ? (
                <CpfInput
                    value={clinicCpfCnpj}
                    onChange={onCpfCnpjChange}
                    required
                    label={getTranslation(T.responsibleCpf)}
                    inputTestId={`${idPrefix}-cpf`}
                    readOnly={cpfLocked}
                />
            ) : clinicTaxType === ClinicTaxTypes.Cnpj ? (
                <div>
                    <Label htmlFor={`${idPrefix}-cnpj`} className="text-sm font-medium text-foreground">
                        {getTranslation(T.clinicCnpj)}
                    </Label>
                    <Input
                        id={`${idPrefix}-cnpj`}
                        value={formatCnpjDisplay(clinicCpfCnpj)}
                        onChange={(e) => onCpfCnpjChange(normalizeCnpjDigits(e.target.value))}
                        placeholder="00.000.000/0000-00"
                        inputMode="numeric"
                        required
                        maxLength={18}
                        className="mt-2 rounded-xl"
                        data-testid={`${idPrefix}-cnpj`}
                    />
                </div>
            ) : null}

            <div>
                <Label htmlFor={`${idPrefix}-phone`} className="text-sm font-medium text-foreground">
                    {getTranslation(T.clinicPhone)}
                </Label>
                <Input
                    id={`${idPrefix}-phone`}
                    value={clinicPhone}
                    onChange={(e) => onPhoneChange(e.target.value)}
                    inputMode="tel"
                    className="mt-2 rounded-xl"
                    data-testid={`${idPrefix}-phone`}
                />
            </div>
        </>
    );
}

export function validateClinicTaxIdentityForm(
    clinicTaxType: ClinicTaxType | "",
    clinicCpfCnpj: string,
    clinicName: string,
    clinicPhone: string,
): string | null {
    const taxError = validateClinicTaxId(clinicTaxType, clinicCpfCnpj, isValidCpf);
    if (taxError) {
        return taxError;
    }

    if (clinicName.trim().length < 2) {
        return getTranslation(T.nameRequired);
    }

    const digits = clinicPhone.replace(/\D/g, "");
    if (digits.length > 0 && (digits.length < 10 || digits.length > 11)) {
        return getTranslation(T.phoneInvalid);
    }

    return null;
}
